import { useQueries, useQuery } from "@tanstack/react-query";

import { axiosInstance } from "../../lib/axios";
import { INTEGRATION_SCOPES } from "../constants";

import type { IntegrationScope } from "../constants";
import type {
  ConnectedIntegrationDetail,
  ConnectedIntegrationResponse,
} from "../types/integrations";

export const CONNECTED_INTEGRATIONS_QUERY_KEY = (
  sourceId: string,
  integrationScope: IntegrationScope = INTEGRATION_SCOPES.USER,
) => ["connected-integrations", sourceId, integrationScope] as const;

const connectedIntegrationsQueryOptions = (
  sourceId: string,
  integrationScope: IntegrationScope = INTEGRATION_SCOPES.USER,
) => ({
  queryKey: CONNECTED_INTEGRATIONS_QUERY_KEY(sourceId, integrationScope),
  queryFn: async () =>
    axiosInstance.get<ConnectedIntegrationResponse>(`/connect/${sourceId}`, {
      params: { integration_scope: integrationScope },
    }),
  select: (response: { data: ConnectedIntegrationResponse }) =>
    response.data.results || [],
  retry: false, // Don't retry on 404s - they're expected when no connection exists
});

/**
 * Hook to fetch all connected integration accounts.
 *
 * At the deployment tier each account also carries `organization_integrations` — the
 * workspaces configured under it. That nesting is the only way to read organization
 * rows, so this is also the source of truth for per-workspace configuration.
 */
export const useFetchConnectedIntegrationAccounts = (
  sourceId: string,
  options?: { enabled?: boolean; integrationScope?: IntegrationScope },
) => {
  const integrationScope = options?.integrationScope ?? INTEGRATION_SCOPES.USER;

  return useQuery({
    ...connectedIntegrationsQueryOptions(sourceId, integrationScope),
    enabled: (options?.enabled ?? true) && !!sourceId,
  });
};

export const useFetchConnectedAccountsForSources = (
  sourceIds: string[],
  integrationScope: IntegrationScope = INTEGRATION_SCOPES.USER,
) => {
  const results = useQueries({
    queries: sourceIds.map((sourceId) =>
      connectedIntegrationsQueryOptions(sourceId, integrationScope),
    ),
  });

  const bySource: Record<string, ConnectedIntegrationDetail[]> = {};
  sourceIds.forEach((sourceId, index) => {
    bySource[sourceId] = results[index]?.data ?? [];
  });

  const total = Object.values(bySource).reduce(
    (sum, accounts) => sum + accounts.length,
    0,
  );

  return {
    bySource,
    total,
    anyConnected: total > 0,
    isLoading: results.some((result) => result.isLoading),
  };
};
